/**
 * Application-level route configuration that maps URL paths to page components.
 *
 * Mounts public and protected routes and triggers the authorization check on load.
 *
 * @module routes/common
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Home from '../pages/Home';
import NotFound from '../pages/NotFound';
import Dashboard from '../pages/dashboard/Dashboard.jsx';
import Sims from '../pages/sim/Sims.jsx';
import CreateSims from '../pages/sim/CreateSims';
import Admin from '../pages/admin/Admin.jsx';
import Tools from '../pages/tools/Tools';
import Settings from '../pages/Settings.jsx';
import TakeSim from '../components/sim/takeSim/TakeSim.jsx';
import PermissionDenied from '../pages/PermissionDenied';
import CreateUser from '../components/admin/CreateUser.jsx';
import Login from '../pages/login/Login.jsx';
import ProtectedRouter from './ProtectRoute';
import { checkAuthorization } from '../redux/authentication/action';
import ForgotPassword from '../pages/forgotpassword/ForgotPassword.jsx';
import Helpdesk from "../pages/helpdesk/Helpdesk";

/**
 * Represents the Common component that manages the application's route structure
 * and initializes authorization check on component mount.
 *
 * This component renders multiple routes using `Route` and `ProtectedRouter` components
 * for different paths including login, dashboard, settings, and various feature modules.
 * Additionally, it includes a wildcard route to handle 404 pages.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Function} props.checkAuthorization - A function to verify and enforce user authorization, executed on component mount.
 * @returns {JSX.Element} A JSX element defining the application's routing structure.
 */
const Common = ({ checkAuthorization }) => {
    useEffect(() => {
        checkAuthorization();
    }, []);

    return (
        <Switch>
            <Route
                path="/login"
                exact
                render={() => (
                    <Login />
                )}
            />
            <Route
                path="/login?allowNewCompany=(:allowNewCompany)"
                exact
                render={() => (
                    <Login />
                )}
            />
            <ProtectedRouter path="/" exact component={Home} />
            <ProtectedRouter
                path="/dashboard"
                title="DASHBOARD"
                exact
                component={Dashboard}
            />
            <ProtectedRouter
                path="/sims"
                title="SIMS"
                exact
                component={Sims}
            />
            <ProtectedRouter
                path="/sims/createsim"
                title="SIMS"
                subtitle="CREATE SIM"
                exact
                component={CreateSims}
            />
            <ProtectedRouter
                path="/sims/createsim?simId=(:simId)"
                title="SIMS"
                subtitle="EDIT SIM"
                exact
                component={CreateSims}
            />
            <ProtectedRouter
                path="/sims/takesim"
                title="SIMS"
                subtitle="TAKE SIM"
                exact
                component={TakeSim}
            />
            <ProtectedRouter
                path="/admin"
                title="ADMIN"
                exact
                component={Admin}
            />
            <ProtectedRouter
                path="/tools"
                title="TOOLS"
                exact
                component={Tools}
            />
            <ProtectedRouter
                path="/settings"
                title="SETTINGS"
                exact
                component={Settings}
            />
            <ProtectedRouter
                path="/helpdesk"
                title="HELPDESK"
                exact
                component={Helpdesk}
            />
            <ProtectedRouter
                path="/admin/createuser"
                title="ADMIN"
                exact
                component={CreateUser}
            />
            <Route
                path="/permissiondenied"
                title="Permission Denied"
                exact
                component={PermissionDenied}
            />
            <Route
                path="/forgotpassword"
                title="Forgot Password"
                exact
                component={ForgotPassword}
            />
            <Route path="*" component={NotFound} />
        </Switch>
    );
};

Common.propTypes = {
    checkAuthorization: PropTypes.func,
};

/**
 * Maps Redux dispatch to the Common component props.
 *
 * @type {Object}
 * @property {Function} checkAuthorization - Dispatches the authorization check action.
 */
const mapDispatchToProps = {
    checkAuthorization,
};

export default connect(null, mapDispatchToProps)(Common);
